import { createBrowserRouter } from "react-router-dom";
import App from "@/App";
import Dashboard from "@/pages/Dashboard";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import NotFound from "@/pages/NotFound";
import ProtectedRoute from "@/components/ProtectedRoute";
import { generateEmbedRoutes } from "@/utils/embedRoutes.tsx";

// Lazy load page components
import { lazy } from "react";

// Auth pages
const AuthLogin = lazy(() => import("@/pages/(auth)/login/page"));
const AuthRegister = lazy(() => import("@/pages/(auth)/register/page"));
const AuthForgotPassword = lazy(
  () => import("@/pages/(auth)/forgot-password/page"),
);
const ResetPassword = lazy(() => import("@/pages/(auth)/reset-password/page"));
const InitiateUnifiedSignin = lazy(
  () => import("@/pages/auth/initiate-unified-signin/page"),
);

// Chat pages
const Chat = lazy(() => import("@/pages/(chat)/page"));
const ChatById = lazy(() => import("@/pages/(chat)/chat/[id]/page"));

// Application pages
const Browser = lazy(() => import("@/pages/browser/page"));
const BrowserById = lazy(() => import("@/pages/browser/[id]/page"));
const CallSMS = lazy(() => import("@/pages/call-sms/page"));
const CallSMSById = lazy(() => import("@/pages/call-sms/[id]/page"));
const CallSMSInbound = lazy(() => import("@/pages/call-sms/inbound/page"));
const CallSMSInboundById = lazy(
  () => import("@/pages/call-sms/inbound/[id]/page"),
);
const CMM = lazy(() => import("@/pages/cmm/page"));
const DashboardPage = lazy(() => import("@/pages/dashboard/page"));
const DesktopWrite = lazy(() => import("@/pages/desktop-write/page"));
const DesktopWriteSequence = lazy(
  () => import("@/pages/desktop-write/sequence/page"),
);
const FailureManagement = lazy(() => import("@/pages/failure-management/page"));
const FeatureNotAvailable = lazy(
  () => import("@/pages/feature-not-available/page"),
);
const Forms = lazy(() => import("@/pages/forms/page"));
const Formulary = lazy(() => import("@/pages/formulary/page"));
const Integrations = lazy(() => import("@/pages/integrations/page"));
const Mail = lazy(() => import("@/pages/mail/page"));
const MedRec = lazy(() => import("@/pages/med-rec/page"));
const MedRecById = lazy(() => import("@/pages/med-rec/[id]/page"));
const MedReview = lazy(() => import("@/pages/med-review/page"));
const MyFlows = lazy(() => import("@/pages/my-flows/page"));
const Scheduler = lazy(() => import("@/pages/scheduler/page"));
const Settings = lazy(() => import("@/pages/settings/page"));

const PriorAuth = lazy(() => import("@/pages/prior-auth/page"));
const PriorAuthById = lazy(() => import("@/pages/prior-auth/[id]/page"));
const Profile = lazy(() => import("@/pages/profile/page"));
const Scanner = lazy(() => import("@/pages/scanner/page"));
const ScannerById = lazy(() => import("@/pages/scanner/[id]/page"));
const SettingsDebug = lazy(() => import("@/pages/settings/debug/page"));
const SettingsIntegrations = lazy(
  () => import("@/pages/settings/integrations/page"),
);
const StoragePriorAuth = lazy(() => import("@/pages/storage/prior-auth/page"));
const Team = lazy(() => import("@/pages/team/page"));
const Tenant = lazy(() => import("@/pages/tenant/page"));

export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <App />,
      errorElement: <NotFound />,
      children: [
        // Home route - redirects to Chat (the main landing page)
        {
          index: true,
          element: (
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          ),
        },

        // Auth routes (public)
        {
          path: "/login",
          element: <Login />,
        },
        {
          path: "/register",
          element: <Register />,
        },
        {
          path: "/forgot-password",
          element: <ForgotPassword />,
        },
        {
          path: "/auth/login",
          element: <AuthLogin />,
        },
        {
          path: "/auth/register",
          element: <AuthRegister />,
        },
        {
          path: "/auth/forgot-password",
          element: <AuthForgotPassword />,
        },
        {
          path: "/auth/reset-password",
          element: <ResetPassword />,
        },
        {
          path: "/auth/initiate-unified-signin",
          element: <InitiateUnifiedSignin />,
        },

        // Dashboard alternative route
        {
          path: "/dashboard",
          element: (
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          ),
        },

        // Chat routes
        {
          path: "/chat",
          element: (
            <ProtectedRoute>
              <Chat />
            </ProtectedRoute>
          ),
        },
        {
          path: "/chat/:id",
          element: (
            <ProtectedRoute>
              <ChatById />
            </ProtectedRoute>
          ),
        },

        // Browser automation routes
        {
          path: "/browser",
          element: (
            <ProtectedRoute>
              <Browser />
            </ProtectedRoute>
          ),
        },
        {
          path: "/browser/:id",
          element: (
            <ProtectedRoute>
              <BrowserById />
            </ProtectedRoute>
          ),
        },

        // Call & SMS routes
        {
          path: "/call-sms",
          element: (
            <ProtectedRoute>
              <CallSMS />
            </ProtectedRoute>
          ),
        },
        {
          path: "/call-sms/:id",
          element: (
            <ProtectedRoute>
              <CallSMSById />
            </ProtectedRoute>
          ),
        },
        {
          path: "/call-sms/inbound",
          element: (
            <ProtectedRoute>
              <CallSMSInbound />
            </ProtectedRoute>
          ),
        },
        {
          path: "/call-sms/inbound/:id",
          element: (
            <ProtectedRoute>
              <CallSMSInboundById />
            </ProtectedRoute>
          ),
        },

        // Care Management (CMM) routes
        {
          path: "/cmm",
          element: (
            <ProtectedRoute>
              <CMM />
            </ProtectedRoute>
          ),
        },

        // Desktop write routes
        {
          path: "/desktop-write",
          element: (
            <ProtectedRoute>
              <DesktopWrite />
            </ProtectedRoute>
          ),
        },
        {
          path: "/desktop-write/sequence",
          element: (
            <ProtectedRoute>
              <DesktopWriteSequence />
            </ProtectedRoute>
          ),
        },

        // Mail routes
        {
          path: "/mail",
          element: (
            <ProtectedRoute>
              <Mail />
            </ProtectedRoute>
          ),
        },

        // Medical reconciliation routes
        {
          path: "/med-rec",
          element: (
            <ProtectedRoute>
              <MedRec />
            </ProtectedRoute>
          ),
        },
        {
          path: "/med-rec/:id",
          element: (
            <ProtectedRoute>
              <MedRecById />
            </ProtectedRoute>
          ),
        },

        // Medication review routes
        {
          path: "/med-review",
          element: (
            <ProtectedRoute>
              <MedReview />
            </ProtectedRoute>
          ),
        },
        {
          path: "/prior-auth",
          element: (
            <ProtectedRoute>
              <PriorAuth />
            </ProtectedRoute>
          ),
        },
        {
          path: "/prior-auth/:id",
          element: (
            <ProtectedRoute>
              <PriorAuthById />
            </ProtectedRoute>
          ),
        },

        // Scanner routes
        {
          path: "/scanner",
          element: (
            <ProtectedRoute>
              <Scanner />
            </ProtectedRoute>
          ),
        },
        {
          path: "/scanner/:id",
          element: (
            <ProtectedRoute>
              <ScannerById />
            </ProtectedRoute>
          ),
        },

        // Scheduler routes
        {
          path: "/scheduler",
          element: (
            <ProtectedRoute>
              <Scheduler />
            </ProtectedRoute>
          ),
        },
        {
          path: "/failure-management",
          element: (
            <ProtectedRoute>
              <FailureManagement />
            </ProtectedRoute>
          ),
        },
        {
          path: "/feature-not-available",
          element: (
            <ProtectedRoute>
              <FeatureNotAvailable />
            </ProtectedRoute>
          ),
        },
        {
          path: "/forms",
          element: (
            <ProtectedRoute>
              <Forms />
            </ProtectedRoute>
          ),
        },
        {
          path: "/formulary",
          element: (
            <ProtectedRoute>
              <Formulary />
            </ProtectedRoute>
          ),
        },
        {
          path: "/integrations",
          element: (
            <ProtectedRoute>
              <Integrations />
            </ProtectedRoute>
          ),
        },
        {
          path: "/my-flows",
          element: (
            <ProtectedRoute>
              <MyFlows />
            </ProtectedRoute>
          ),
        },
        {
          path: "/profile",
          element: (
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          ),
        },
        {
          path: "/team",
          element: (
            <ProtectedRoute>
              <Team />
            </ProtectedRoute>
          ),
        },
        {
          path: "/tenant",
          element: (
            <ProtectedRoute>
              <Tenant />
            </ProtectedRoute>
          ),
        },

        // Settings routes
        {
          path: "/settings",
          element: (
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          ),
        },
        {
          path: "/settings/debug",
          element: (
            <ProtectedRoute>
              <SettingsDebug />
            </ProtectedRoute>
          ),
        },
        {
          path: "/settings/integrations",
          element: (
            <ProtectedRoute>
              <SettingsIntegrations />
            </ProtectedRoute>
          ),
        },

        // Storage routes
        {
          path: "/storage/prior-auth",
          element: (
            <ProtectedRoute>
              <StoragePriorAuth />
            </ProtectedRoute>
          ),
        },

        // Auto-generated embed routes - no need to manually define each one!
        ...generateEmbedRoutes(),

        // Catch all route
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
    },
  },
);
